import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { BACKEND_URL } from "../../config";

export const tasksApi = createApi({
  reducerPath: "tasksApi",
  tagTypes: ['RecentEmployees'],
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        // Include token in request header
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
    fetchFn: async (input, init) => {
      if (!input) {
        return new Response(null, {
          status: 400,
          statusText: "Invalid request",
        });
      }

      const response = await fetch(input, init);

      if (response.status === 204) {
        // Return a transformed response to signal no content
        return new Response(JSON.stringify([]), { status: 200 });
      }

      return response;
    },
  }),
  endpoints: (builder) => ({
    getAllTasks: builder.query({
      query: () => "/task/portal/fetch-all-tasks",
    }),
    getAllEmployeeTasks: builder.query({
      query: () => "/task/portal/get-all-employee-tasks",
    }),
    updateTaskManagerRecord: builder.mutation({
      query: ({ recordId, fields }) => ({
        url: "/task/portal/update-record-task-manager",
        method: "POST",
        body: { recordId, fields },
      }),
    }),
    searchEmployeesReTrigger: builder.query({
      query: (term) => `/task/portal/search-employees-re-trigger?term=${encodeURIComponent(term)}`,
    }),
    updateReTriggerTask: builder.mutation({
      query: (employeeIds) => ({
        url: "/task/portal/update-re-trigger-task",
        method: "POST",
        body: { employeeIds },
      }),
    }),
    overrideTaskCompletion: builder.mutation({
      query: ({ taskNumber, inCompletedTask }) => ({
        url: "/task/portal/override-task-completion",
        method: "POST",
        body: { taskNumber, markOverrideCompleted: "true", ...(inCompletedTask && { inCompletedTask: true }) },
      }),
    }),
    getTaskTypeFieldOptions: builder.query({
      query: () => "/task-type/portal/task-type-field-options",
    }),
    createTaskType: builder.mutation({
      query: (body) => ({
        url: "/task-type/portal/create-task-type",
        method: "POST",
        body,
      }),
    }),
    deleteTaskType: builder.mutation({
      query: (taskTypeRecordId) => ({
        url: "/task-type/portal/delete-task-type",
        method: "POST",
        body: { taskTypeRecordId },
      }),
    }),
    getTaskTypesByCreator: builder.mutation({
      query: (taskTypeCreatorId) => ({
        url: "/task-type/portal/get-task-types-by-creator",
        method: "POST",
        body: { taskTypeCreatorId },
      }),
    }),
    getAllTaskTypes: builder.mutation({
      query: () => ({
        url: "/task-type/portal/get-all-task-types",
        method: "POST",
        body: {},
      }),
    }),
    // Single Task Type by record ID (admin-gated on the BFF). Used by the
    // Manage Task Types duplicate flow, which needs the FULL record — the
    // list endpoint returns a field-limited set to keep the payload small.
    getTaskType: builder.mutation({
      query: (taskTypeRecordId) => ({
        url: "/task-type/portal/get-task-type",
        method: "POST",
        body: { taskTypeRecordId },
      }),
    }),
    updateTaskType: builder.mutation({
      query: (body) => ({
        url: "/task-type/portal/update-task-type",
        method: "POST",
        body,
      }),
    }),
    getRepTeamForTaskType: builder.query({
      query: () => "/task-type/portal/rep-team-for-task-type",
    }),
    markEventAttendance: builder.mutation({
      query: ({ eventAttendance, recId }) => ({
        url: "/task/portal/mark-event-attendance",
        method: "PATCH",
        body: { eventAttendance, recId },
      }),
    }),
    createDirectTask: builder.mutation({
      query: (body) => ({
        url: "/task-type/portal/create-task-type",
        method: "POST",
        body,
      }),
    }),
    createOneOffTask: builder.mutation({
      query: (body) => ({
        url: "/task-type/portal/create-one-off-task",
        method: "POST",
        body,
      }),
    }),
    getOneOffTasks: builder.mutation({
      query: (body) => ({
        url: "/task-type/portal/get-one-off-tasks",
        method: "POST",
        body,
      }),
    }),
    getPersonalBestTaskTypes: builder.mutation({
      query: ({ employeeRecordId }) => ({
        url: "/task-type/portal/get-personal-best-task-types",
        method: "POST",
        body: { employeeRecordId },
      }),
    }),
    assignEmployeeToTaskType: builder.mutation({
      query: ({ employeeRecordId, taskTypeRecordId, action }) => ({
        url: "/task-type/portal/assign-employee-to-task-type",
        method: "POST",
        body: { employeeRecordId, taskTypeRecordId, action },
      }),
    }),
    getAllActiveEmployees: builder.query({
      query: (opts = {}) => {
        const params = [];
        if (opts && opts.excludeTerminated) params.push("excludeTerminated=true");
        if (opts && opts.includeFriends) params.push("includeFriends=true");
        const qs = params.length ? `?${params.join("&")}` : "";
        return `/createEmployee/portal/get-all-active-employees${qs}`;
      },
      // Thread 1a03b62e232f21dd (round 10): without a tag this query never
      // refetches on cache invalidation — a half-filled tournament wizard stays
      // mounted, so a person created mid-wizard never appears until the wizard
      // remounts. Tagging it lets createNewEmployee/updateEmployee invalidation
      // refresh the roster in place.
      providesTags: ['RecentEmployees'],
    }),
    deleteOneOffTask: builder.mutation({
      query: (recordIds) => ({
        url: "/task-type/portal/delete-one-off-task",
        method: "POST",
        body: { recordId: Array.isArray(recordIds) ? recordIds : [recordIds] },
      }),
    }),
    getAiPrompt: builder.mutation({
      query: (aiPromptRecordId) => ({
        url: "/ai-prompt/portal/get-ai-prompt",
        method: "POST",
        body: { aiPromptRecordId },
      }),
    }),
    updateAiPrompt: builder.mutation({
      query: (body) => ({
        url: "/ai-prompt/portal/update-ai-prompt",
        method: "POST",
        body,
      }),
    }),
    // Active reps whose task (for a given Task Type) is Yellow (submitted, not
    // graded) or Red (incomplete) — powers the Retrigger AI Grading list modal.
    getAiRegradeCandidates: builder.query({
      query: (taskTypeRecordId) =>
        `/task/portal/ai-regrade-candidates?taskTypeRecordId=${encodeURIComponent(taskTypeRecordId)}`,
    }),
    listNewHirePositions: builder.query({
      query: () => '/createEmployee/portal/positions',
    }),
    listRepAgreements: builder.query({
      query: (positionRecIds) => ({
        url: '/createEmployee/portal/rep-agreements',
        params: { positionRecIds: (positionRecIds || []).join(',') },
      }),
    }),
    listNewHireLocations: builder.query({
      query: () => '/createEmployee/portal/locations',
    }),
    createNewEmployee: builder.mutation({
      query: (body) => ({
        url: '/createEmployee/portal/new-employee',
        method: 'POST',
        body,
      }),
      // Thread 1a03b62e232f21dd: a new hire must appear in an already-mounted
      // wizard roster picker without a page refresh.
      invalidatesTags: ['RecentEmployees'],
    }),
    uploadNewEmployeeDrugTest: builder.mutation({
      query: (formData) => ({
        url: '/createEmployee/portal/upload-drug-test',
        method: 'POST',
        body: formData,
      }),
    }),
    getRecentEmployees: builder.query({
      query: () => '/createEmployee/portal/recent-employees',
      providesTags: ['RecentEmployees'],
    }),
    updateEmployee: builder.mutation({
      query: ({ recId, ...body }) => ({
        url: `/createEmployee/portal/update-employee/${recId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['RecentEmployees', 'ActiveTechs'],
    }),
    searchTaskHistory: builder.query({
      query: ({ q = "", days = 365, page = 1 } = {}) =>
        `/task/portal/history-search?q=${encodeURIComponent(q)}&days=${days}&page=${page}`,
    }),
    getTaskHistoryTypes: builder.query({
      query: () => `/task/portal/history-types`,
    }),
    resendTechOnboarding: builder.mutation({
      query: (recId) => ({
        url: `/createEmployee/portal/resend-tech-onboarding/${recId}`,
        method: 'POST',
      }),
    }),
    triggerVtrForEmployee: builder.mutation({
      query: (recId) => ({
        url: `/createEmployee/portal/trigger-vtr/${recId}`,
        method: 'POST',
      }),
    }),
    updateEmployeeFile: builder.mutation({
      query: ({ recId, fieldKey, file }) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('fieldKey', fieldKey);
        return {
          url: `/createEmployee/portal/update-employee-file/${recId}`,
          method: 'POST',
          body: fd,
        };
      },
      invalidatesTags: ['ActiveTechs'],
    }),
    sendTechLogin: builder.mutation({
      query: (recId) => ({
        url: `/createEmployee/portal/send-tech-login/${recId}`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetAllTasksQuery,
  useGetAllEmployeeTasksQuery,
  useUpdateTaskManagerRecordMutation,
  useSearchEmployeesReTriggerQuery,
  useUpdateReTriggerTaskMutation,
  useOverrideTaskCompletionMutation,
  useGetTaskTypeFieldOptionsQuery,
  useCreateTaskTypeMutation,
  useDeleteTaskTypeMutation,
  useGetTaskTypesByCreatorMutation,
  useGetAllTaskTypesMutation,
  useGetTaskTypeMutation,
  useUpdateTaskTypeMutation,
  useGetRepTeamForTaskTypeQuery,
  useMarkEventAttendanceMutation,
  useCreateDirectTaskMutation,
  useCreateOneOffTaskMutation,
  useGetOneOffTasksMutation,
  useGetPersonalBestTaskTypesMutation,
  useAssignEmployeeToTaskTypeMutation,
  useGetAllActiveEmployeesQuery,
  useDeleteOneOffTaskMutation,
  useGetAiPromptMutation,
  useUpdateAiPromptMutation,
  useGetAiRegradeCandidatesQuery,
  useListNewHirePositionsQuery,
  useListRepAgreementsQuery,
  useListNewHireLocationsQuery,
  useCreateNewEmployeeMutation,
  useUploadNewEmployeeDrugTestMutation,
  useGetRecentEmployeesQuery,
  useUpdateEmployeeMutation,
  useSearchTaskHistoryQuery,
  useGetTaskHistoryTypesQuery,
  useResendTechOnboardingMutation,
  useTriggerVtrForEmployeeMutation,
  useUpdateEmployeeFileMutation,
  useSendTechLoginMutation,
} = tasksApi;